import { enumNarrow } from '../helpers/enumGuard'
import { IBankAccount } from './bankAccount'
import { IEntityInfo } from './entityInfo'

export enum BankTransactionCategory {
  CreditCard = 'credit_card',
  InvestorPayment = 'investment',
  DiligenceDeposit = 'diligence_deposit',
  Debt = 'debt',
  Debtor = 'debtor',
  Vendor = 'vendor',
  Other = 'other',
  IntracompanyTransfer = 'intracompany_transfer',
  Taxes = 'taxes',
  Insurance = 'insurance',
  Payroll = 'payroll',
}

/** Cast-free narrower for BankTransactionCategory (see common/helpers/enumGuard). */
export const asBankTransactionCategory = enumNarrow(BankTransactionCategory).as

/**
 * Maps the optional "Category" column labels accepted on bank-transaction uploads to the
 * internal enum. Keys are normalized (trimmed + lower-cased) before lookup. Every category is
 * accepted on both inflow and outflow rows; anything not listed here is a row-level error.
 */
export const UPLOAD_CATEGORY_LABEL_MAP: Record<string, BankTransactionCategory> = {
  'credit card': BankTransactionCategory.CreditCard,
  investment: BankTransactionCategory.InvestorPayment,
  'diligence deposit': BankTransactionCategory.DiligenceDeposit,
  debt: BankTransactionCategory.Debt,
  'customer collection': BankTransactionCategory.Debtor,
  'vendor payment': BankTransactionCategory.Vendor,
  other: BankTransactionCategory.Other,
  intracompany: BankTransactionCategory.IntracompanyTransfer,
  taxes: BankTransactionCategory.Taxes,
  insurance: BankTransactionCategory.Insurance,
  payroll: BankTransactionCategory.Payroll,
}

/** When multiple categories match, pick one by this priority (first = highest). */
export const CATEGORY_PRIORITY: BankTransactionCategory[] = [
  BankTransactionCategory.CreditCard,
  BankTransactionCategory.InvestorPayment,
  BankTransactionCategory.DiligenceDeposit,
  BankTransactionCategory.Debt,
  BankTransactionCategory.Debtor,
  BankTransactionCategory.Vendor,
  BankTransactionCategory.Other,
  BankTransactionCategory.IntracompanyTransfer,
  BankTransactionCategory.Taxes,
  BankTransactionCategory.Insurance,
  BankTransactionCategory.Payroll,
]

export type PredictionConfidence = 'high' | 'medium' | 'low'

/**
 * The five inflow buckets underwriting works in when reading a raw bank statement. These are
 * the labels UW uses in their Claude-in-Excel prompt; each maps onto one internal
 * `BankTransactionCategory` via `INFLOW_BUCKET_TO_CATEGORY`. Kept as a separate vocabulary
 * because the internal enum names (`debtor`, `investment`) don't read as inflow buckets and
 * are shared with outflows.
 */
export enum InflowBucket {
  CustomerCollection = 'customer_collection',
  InvestmentInflow = 'investment_inflow',
  DebtInflow = 'debt_inflow',
  IntercompanyInflow = 'intercompany_inflow',
  OtherInflow = 'other_inflow',
}

export const INFLOW_BUCKET_TO_CATEGORY: Record<InflowBucket, BankTransactionCategory> = {
  [InflowBucket.CustomerCollection]: BankTransactionCategory.Debtor,
  [InflowBucket.InvestmentInflow]: BankTransactionCategory.InvestorPayment,
  [InflowBucket.DebtInflow]: BankTransactionCategory.Debt,
  [InflowBucket.IntercompanyInflow]: BankTransactionCategory.IntracompanyTransfer,
  [InflowBucket.OtherInflow]: BankTransactionCategory.Other,
}

export const INFLOW_BUCKET_LABELS: Record<InflowBucket, string> = {
  [InflowBucket.CustomerCollection]: 'Customer Collections',
  [InflowBucket.InvestmentInflow]: 'Investment Inflow',
  [InflowBucket.DebtInflow]: 'Debt Inflow',
  [InflowBucket.IntercompanyInflow]: 'Intercompany Inflow',
  [InflowBucket.OtherInflow]: 'Other Inflow',
}

/**
 * The outflow counterpart, one bucket per option the Debits tab offers (`TYPE_OPTIONS_DEBIT`) — a
 * suggestion has to be something an underwriter can actually accept from the dropdown.
 *
 * Inflows still get the larger share of a run's budget — they are what the borrowing base is built
 * from — but outflows are no longer a leftover: `OUTFLOW_BUDGET_SHARE` reserves room for them, so
 * a one-off payment like a diligence deposit can still be reached on a client with hundreds of
 * inflow descriptions.
 */
export enum OutflowBucket {
  VendorPayment = 'vendor_payment',
  Payroll = 'payroll',
  Taxes = 'taxes',
  Insurance = 'insurance',
  CreditCardPayment = 'credit_card_payment',
  DebtOutflow = 'debt_outflow',
  IntercompanyOutflow = 'intercompany_outflow',
  DiligenceDeposit = 'diligence_deposit',
  OtherOutflow = 'other_outflow',
}

export const OUTFLOW_BUCKET_TO_CATEGORY: Record<OutflowBucket, BankTransactionCategory> = {
  [OutflowBucket.VendorPayment]: BankTransactionCategory.Vendor,
  [OutflowBucket.Payroll]: BankTransactionCategory.Payroll,
  [OutflowBucket.Taxes]: BankTransactionCategory.Taxes,
  [OutflowBucket.Insurance]: BankTransactionCategory.Insurance,
  [OutflowBucket.CreditCardPayment]: BankTransactionCategory.CreditCard,
  [OutflowBucket.DebtOutflow]: BankTransactionCategory.Debt,
  [OutflowBucket.IntercompanyOutflow]: BankTransactionCategory.IntracompanyTransfer,
  [OutflowBucket.DiligenceDeposit]: BankTransactionCategory.DiligenceDeposit,
  [OutflowBucket.OtherOutflow]: BankTransactionCategory.Other,
}

export const OUTFLOW_BUCKET_LABELS: Record<OutflowBucket, string> = {
  [OutflowBucket.VendorPayment]: 'Vendor Payment',
  [OutflowBucket.Payroll]: 'Payroll',
  [OutflowBucket.Taxes]: 'Taxes',
  [OutflowBucket.Insurance]: 'Insurance',
  [OutflowBucket.CreditCardPayment]: 'Credit Card',
  [OutflowBucket.DebtOutflow]: 'Debt Outflow',
  [OutflowBucket.IntercompanyOutflow]: 'Intracompany Outflow',
  [OutflowBucket.DiligenceDeposit]: 'Diligence Deposit',
  [OutflowBucket.OtherOutflow]: 'Other Outflow',
}

/** Which side of the statement a description group sits on. Inflow = `amount > 0`. */
export type TransactionDirection = 'inflow' | 'outflow'

/**
 * The Credits/Debits vocabulary the mapping screen tab speaks and `UpdateTransactions` scopes each
 * write by. Distinct from `TransactionDirection` ('inflow'/'outflow'): same split, different words —
 * this is the one the UI, the update payload, and the list response use. Credit = `amount > 0`.
 */
export enum TransactionType {
  Credit = 'Credit',
  Debit = 'Debit',
}

/**
 * Cast-free narrowers used to check a bucket against a row's direction — the prediction schema
 * accepts both vocabularies in one enum, so the pairing is enforced at resolve time.
 */
export const asInflowBucket = enumNarrow(InflowBucket).as
export const asOutflowBucket = enumNarrow(OutflowBucket).as

/**
 * How a customer collection reached the borrower. Drives which kind of entity is expected:
 * `Wholesale` → the retailer/distributor, `DTC` → the marketplace or payment processor
 * (stored as an intermediary once accepted), `Check` → no identifiable payer.
 */
export enum CollectionChannel {
  Wholesale = 'wholesale',
  DTC = 'dtc',
  Check = 'check',
  Other = 'other',
}

/** Which predictor produced a `bank_transactions` prediction. */
export enum BankPredictionSource {
  /** dwightapi's rule/ML pipeline — runs on every ingested transaction, inflow and outflow. */
  DwightApi = 'dwightapi',
  /** Atlas's LLM mapping pass. Takes precedence over `DwightApi`. */
  Atlas = 'atlas',
  /**
   * Portfolio consensus: the same memo mapped to the same category by enough other borrowers that
   * Atlas reuses their answer instead of spending a model call on it.
   */
  Consensus = 'consensus',
}

/**
 * The sources that mean "Atlas has already answered this description group". Both the LLM pass and
 * the consensus pass write a suggestion an underwriter can act on, so both have to suppress a
 * re-run — otherwise the cheap pass would hand its groups straight back to the expensive one.
 */
export const ATLAS_PREDICTION_SOURCES: BankPredictionSource[] = [
  BankPredictionSource.Atlas,
  BankPredictionSource.Consensus,
]

export interface IPredictionMetadata {
  reason?: string
  amount?: number
  /** Which predictor wrote this prediction. Absent on rows predicted before this was tracked. */
  source?: BankPredictionSource
  /** Set by Atlas on customer collections. */
  collectionChannel?: CollectionChannel
  /** Atlas keeps a baseline entity guess it couldn't improve on — this says whose entity is shown. */
  entitySource?: BankPredictionSource
  /** True when Atlas's entity resolved to an existing `entity_info` row rather than a new name. */
  entityMatchedExisting?: boolean
  /** Distinct other borrowers backing a `Consensus` prediction. Absent on every other source. */
  consensusClients?: number
  model?: string
  predictedAt?: string
  /** The dwightapi prediction Atlas superseded, kept so nothing is lost by prioritizing Atlas. */
  baseline?: {
    category?: string | null
    confidence?: string | null
    entity?: string | null
  } | null
  [key: string]: unknown
}

export interface IBankTransactionData {
  data?: IBankTransaction[]
  totalCount?: number
  bankAccounts?: IBankAccount[]
}
export interface IBankTransaction {
  id: string
  cleanedDescription: string
  description?: string
  predictedCategory?: BankTransactionCategory | null
  entityPrediction?: string | null
  predictionConfidence?: PredictionConfidence | null
  predictionMetadata?: IPredictionMetadata | null
  category?: BankTransactionCategory | null
  entityPredictionId?: string
  entityName?: string | null
  intermediaryName?: string | null
  transactionType?: TransactionType
  transactionAmount?: number
  totalItems?: number
  rows: IBankTransactionDetail[]
}

export interface IBankTransactionDetail {
  id: string
  descriptionOriginal: string
  recordDate: string
  amount: number
  totalCount?: number
}

/** @deprecated Legacy flag-based category values */
export enum LegacyBankTransactionCategory {
  Deposit = 'diligence_deposit_flag',
  VendorPayment = 'vendor_payment_flag',
  Intracompany = 'intra_company_transfer_flag',
  Debt = 'debt_flag',
  Other = 'other',
  CustomerCollection = 'debtor_payment_flag',
  Investment = 'investment_flag',
  Taxes = 'taxes_flag',
  CreditCards = 'credit_cards_flag',
  Insurance = 'insurance_flag',
  Payroll = 'payroll_flag',
}

export const TYPE_OPTIONS_DEBIT: { value: string | string[]; label: string }[] = [
  {
    value: [
      BankTransactionCategory.Debt,
      BankTransactionCategory.IntracompanyTransfer,
      BankTransactionCategory.DiligenceDeposit,
      BankTransactionCategory.Vendor,
      BankTransactionCategory.Taxes,
      BankTransactionCategory.CreditCard,
      BankTransactionCategory.Insurance,
      BankTransactionCategory.Payroll,
    ],
    label: 'All',
  },
  {
    value: BankTransactionCategory.Vendor,
    label: 'Vendor Payment',
  },
  {
    value: BankTransactionCategory.IntracompanyTransfer,
    label: 'Intracompany Outflow',
  },
  {
    value: BankTransactionCategory.Debt,
    label: 'Debt Outflow',
  },
  {
    value: BankTransactionCategory.DiligenceDeposit,
    label: 'Diligence Deposit',
  },
  {
    value: BankTransactionCategory.Taxes,
    label: 'Taxes',
  },
  {
    value: BankTransactionCategory.CreditCard,
    label: 'Credit Card',
  },
  {
    value: BankTransactionCategory.Insurance,
    label: 'Insurance',
  },
  {
    value: BankTransactionCategory.Payroll,
    label: 'Payroll',
  },
  {
    value: BankTransactionCategory.Other,
    label: 'Other Outflow',
  },
]

export const TYPE_OPTIONS_CREDIT: {
  value: string | string[]
  label: string
}[] = [
  {
    value: [
      BankTransactionCategory.Debt,
      BankTransactionCategory.Debtor,
      BankTransactionCategory.InvestorPayment,
      BankTransactionCategory.IntracompanyTransfer,
      BankTransactionCategory.Other,
    ],
    label: 'All',
  },
  {
    value: BankTransactionCategory.Debtor,
    label: 'Customer Collection',
  },
  {
    value: BankTransactionCategory.InvestorPayment,
    label: 'Investment Inflow',
  },
  {
    value: BankTransactionCategory.Debt,
    label: 'Debt Inflow',
  },
  {
    value: BankTransactionCategory.IntracompanyTransfer,
    label: 'Intracompany Inflow',
  },
  {
    value: BankTransactionCategory.Other,
    label: 'Other Inflow',
  },
]

export const BANK_CATEGORY_OPTIONS = [
  {
    value: BankTransactionCategory.CreditCard,
    label: 'Credit Card',
  },
  {
    value: BankTransactionCategory.InvestorPayment,
    label: 'Investment',
  },
  {
    value: BankTransactionCategory.DiligenceDeposit,
    label: 'Diligence Deposit',
  },
  {
    value: BankTransactionCategory.Debt,
    label: 'Debt',
  },
  {
    value: BankTransactionCategory.Debtor,
    label: 'Customer Collection',
  },
  {
    value: BankTransactionCategory.Vendor,
    label: 'Vendor Payment',
  },
  {
    value: BankTransactionCategory.Other,
    label: 'Other',
  },
  {
    value: BankTransactionCategory.IntracompanyTransfer,
    label: 'Intracompany',
  },
  {
    value: BankTransactionCategory.Taxes,
    label: 'Taxes',
  },
  {
    value: BankTransactionCategory.Insurance,
    label: 'Insurance',
  },
  {
    value: BankTransactionCategory.Payroll,
    label: 'Payroll',
  },
]

export const CATEGORIES_VALUES = BANK_CATEGORY_OPTIONS.map(({ value }) => value)

// Categories that should receive Inflow/Outflow suffix based on amount
export const INFLOW_OUTFLOW_CATEGORIES = [
  BankTransactionCategory.Debt,
  BankTransactionCategory.InvestorPayment,
  BankTransactionCategory.IntracompanyTransfer,
  BankTransactionCategory.Other,
]

export const CATEGORIES_LABELS: Record<BankTransactionCategory, string> =
  BANK_CATEGORY_OPTIONS.reduce(
    (result, item) => ({
      ...result,
      [item.value.toString()]: item.label,
    }),
    {} as Record<BankTransactionCategory, string>,
  )

export enum BANK_TRANSACTIONS_TABS {
  ACCOUNTS = 'Accounts',
  DEBITS = 'Debits',
  CREDITS = 'Credits',
}

export interface IBankTransactionRaw {
  id: string
  recordDate: string
  description: string
  category: BankTransactionCategory | null
  predictedCategory?: BankTransactionCategory | null
  entityPrediction?: string | null
  predictionConfidence?: PredictionConfidence | null
  entityName: string | null
  entityInfo?: IEntityInfo | null
  intermediaryName: string | null
  intermediaryEntityInfo?: IEntityInfo | null
  amount: number
  clientName?: string
  clientInfo?: {
    id: string
    clientName: string
  }
  plaidAccountId?: string | null
  plaidPredictedCategory?: string | null
  merchant?: string | null
  descriptionOriginal?: string | null
  createdAt?: string | Date | null
  // True when this (client, memo) has a standing "not a diligence deposit" correction — drives the
  // undo affordance on the Transactions view.
  isDiligenceDepositRejected?: boolean
}

export interface IBankTransactionRawData {
  data: IBankTransactionRaw[]
  totals: {
    totalItems: number
    boxLink?: string
    minDate?: string
    maxDate?: string
  }
}

export enum BANK_TRANSACTIONS_GRAPH_OPTION {
  Category = 'category',
  Entity = 'entityName',
}

export type BankTransactionsGraphOption = BANK_TRANSACTIONS_GRAPH_OPTION

export interface IBankTransactionGraph {
  category?: string
  entityName?: string
  amounts: number[]
}

export interface IBankTransactionGraphData {
  headers: string[]
  data: IBankTransactionGraph[]
  option: BANK_TRANSACTIONS_GRAPH_OPTION
}

export const BANK_TRANSACTIONS_GRAPH_GROUP_BY_OPTIONS = [
  {
    value: BANK_TRANSACTIONS_GRAPH_OPTION.Category,
    label: 'Category',
  },
  {
    value: BANK_TRANSACTIONS_GRAPH_OPTION.Entity,
    label: 'Entity',
  },
]

export interface IPendingCashDominion {
  entity_id: string
  entity_name: string
  record_date: string
  amount: number
  waived?: boolean
  waiver_reason?: string | null
}

export interface IPendingCashDominionData {
  data: IPendingCashDominion[]
  totals: {
    totalItems: number
  }
}

export interface IAllPendingCashDominion {
  entity_id: string
  entity_name: string
  client_id: string
  client_name: string
  record_date: string
  amount: number
  waived?: boolean
  waiver_reason?: string | null
}

export interface IAllPendingCashDominionData {
  data: IAllPendingCashDominion[]
  totals: {
    totalItems: number
  }
}
